// ── VSJ icon system ───────────────────────────────────────────────────────────
// Single stroke weight (1.6), round caps, 24px grid. currentColor throughout so
// icons inherit the text color of their context. No emoji, no Unicode glyphs.

interface IconProps {
  size?: number;
  className?: string;
}

const BASE = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

function Svg({ size = 20, className, children }: IconProps & { children: React.ReactNode }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} className={className} {...BASE}>
      {children}
    </svg>
  );
}

/** Brand mark: a place emitting a reading — point + expanding arcs. */
export function VsjBrandMark({ size = 20, className }: IconProps) {
  return (
    <Svg size={size} className={className}>
      <circle cx="12" cy="17" r="1.8" fill="currentColor" stroke="none" />
      <path d="M8.5 13.5a5 5 0 0 1 7 0" />
      <path d="M5.5 10a9.2 9.2 0 0 1 13 0" />
    </Svg>
  );
}

export function IconSearch({ size, className }: IconProps) {
  return (
    <Svg size={size} className={className}>
      <circle cx="11" cy="11" r="6" />
      <path d="M15.5 15.5 20 20" />
    </Svg>
  );
}

export function IconPin({ size, className }: IconProps) {
  return (
    <Svg size={size} className={className}>
      <path d="M12 21s-6.5-5.6-6.5-10.4a6.5 6.5 0 0 1 13 0C18.5 15.4 12 21 12 21z" />
      <circle cx="12" cy="10.5" r="2.2" />
    </Svg>
  );
}

export function IconArrowLeft({ size, className }: IconProps) {
  return (
    <Svg size={size} className={className}>
      <path d="M14.5 5.5 8 12l6.5 6.5" />
    </Svg>
  );
}

/** Crossroads — "cuadra bien conectada". */
export function IconCrossroad({ size, className }: IconProps) {
  return (
    <Svg size={size} className={className}>
      <path d="M12 3v18" />
      <path d="M3 12h18" />
      <circle cx="12" cy="12" r="2.4" fill="currentColor" stroke="none" opacity="0.9" />
    </Svg>
  );
}

export function IconHouse({ size, className }: IconProps) {
  return (
    <Svg size={size} className={className}>
      <path d="M4 11.2 12 4.5l8 6.7" />
      <path d="M6.2 9.8V19.5h11.6V9.8" />
      <path d="M10.2 19.5v-5h3.6v5" />
    </Svg>
  );
}

export function IconBuilding({ size, className }: IconProps) {
  return (
    <Svg size={size} className={className}>
      <rect x="6.5" y="3.5" width="11" height="17" rx="1" />
      <path d="M10 7.5h1.4M12.9 7.5h1.4M10 11h1.4M12.9 11h1.4M10 14.5h1.4M12.9 14.5h1.4" />
      <path d="M10.7 20.5v-3h2.6v3" />
    </Svg>
  );
}

/** Land plot — dashed boundary with a marked point. Reads as "terreno". */
export function IconLand({ size, className }: IconProps) {
  return (
    <Svg size={size} className={className}>
      <rect x="4" y="6.5" width="16" height="12" rx="1" strokeDasharray="3.2 2.6" />
      <circle cx="12" cy="12.5" r="1.8" fill="currentColor" stroke="none" />
    </Svg>
  );
}

/** Storefront — "servicios cercanos". */
export function IconStore({ size, className }: IconProps) {
  return (
    <Svg size={size} className={className}>
      <path d="M4.5 9.5 5.8 4.5h12.4l1.3 5" />
      <path d="M5.5 9.5V19.5h13V9.5" />
      <path d="M9.8 19.5v-4.8h4.4v4.8" />
    </Svg>
  );
}

/** Pulse line — "actividad del entorno". */
export function IconPulse({ size, className }: IconProps) {
  return (
    <Svg size={size} className={className}>
      <path d="M3 12.5h3.6l2.2-5.5 4 10 2.2-4.5H21" />
    </Svg>
  );
}

export function IconCheck({ size, className }: IconProps) {
  return (
    <Svg size={size} className={className}>
      <path d="m5 12.5 4.5 4.5L19 7.5" />
    </Svg>
  );
}

export function IconReport({ size, className }: IconProps) {
  return (
    <Svg size={size} className={className}>
      <path d="M7 3.5h6.5L18 8v12.5H7z" />
      <path d="M13.5 3.5V8H18" />
      <path d="M9.5 12.5h5M9.5 15.8h5" />
    </Svg>
  );
}

export function IconChat({ size, className }: IconProps) {
  return (
    <Svg size={size} className={className}>
      <path d="M4.5 5.5h15v10.5H10L5.5 20v-4H4.5z" />
    </Svg>
  );
}

/** Three-bar confidence indicator. Level 1–3, remaining bars stay faint. */
export function ConfidenceBars({ level = 2 }: { level?: 1 | 2 | 3 }) {
  return (
    <span className="vsj-confbars" aria-hidden="true">
      {[1, 2, 3].map((i) => (
        <span
          key={i}
          className={'vsj-confbar' + (i <= level ? ' vsj-confbar--on' : '')}
          style={{ height: 4 + i * 3 }}
        />
      ))}
    </span>
  );
}
